import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cn } from "@/lib/utils";
import { Icon } from "../icon";

type ThumbnailSize = "xs" | "sm" | "md" | "lg" | "xl";

type ThumbnailBaseProps = {
  size?: ThumbnailSize;
  selected?: boolean;
};

type StaticEmptyThumbnailProps = ThumbnailBaseProps & {
  src?: undefined;
  alt?: never;
  onClick?: never;
  "aria-label"?: string;
};

type StaticImageThumbnailProps = ThumbnailBaseProps & {
  src: string;
  alt: string;
  onClick?: never;
  "aria-label"?: string;
};

type ClickableEmptyThumbnailProps = ThumbnailBaseProps & {
  src?: undefined;
  alt?: never;
  "aria-label": string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
};

type ClickableThumbnailProps = ThumbnailBaseProps & {
  src: string;
  alt: string;
  "aria-label": string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
};

export type ThumbnailProps =
  | StaticEmptyThumbnailProps
  | StaticImageThumbnailProps
  | ClickableEmptyThumbnailProps
  | ClickableThumbnailProps;

const thumbnailSizeClass: Record<ThumbnailSize, string> = {
  xs: "size-(--spacing-40)",
  sm: "size-[60px]",
  md: "size-(--spacing-80)",
  lg: "size-[104px]",
  xl: "size-[120px]",
};

const thumbnailDimension: Record<ThumbnailSize, number> = {
  xs: 40,
  sm: 60,
  md: 80,
  lg: 104,
  xl: 120,
};

const emptyThumbnailStrokeDasharray: Record<ThumbnailSize, string> = {
  xs: "6 6",
  sm: "8 6",
  md: "8 6",
  lg: "8 6",
  xl: "8 6",
};

const emptyThumbnailStrokeDashoffset: Record<ThumbnailSize, number> = {
  xs: 4,
  sm: 4,
  md: 0,
  lg: 2,
  xl: 0,
};

/**
 * Square image preview or image placeholder with optional parent-controlled selected state.
 *
 * @example
 * <Thumbnail src={imageUrl} alt="Asset photo" />
 * <Thumbnail src={imageUrl} alt="Asset photo" aria-label="Open image" onClick={handleOpen} selected />
 */
function Thumbnail({
  size = "md",
  src,
  alt,
  selected,
  onClick,
  "aria-label": ariaLabel,
}: ThumbnailProps) {
  const sizeClass = thumbnailSizeClass[size];
  const focusRingOffset =
    size === "sm"
      ? "focus-visible:ring-offset-1"
      : "focus-visible:ring-offset-2";
  const isInteractive = Boolean(onClick);
  const hasSelectedState = selected !== undefined;

  const rootClassName = cn(
    "relative inline-flex items-center justify-center shrink-0 overflow-hidden",
    "rounded-(--radius-sm)",
    selected === false && "opacity-50",
    selected && "outline-solid outline-2 outline-primary outline-offset-2",
    isInteractive && [
      "cursor-pointer",
      "focus-visible:outline-none",
      "focus-visible:ring-2",
      focusRingOffset,
      "focus-visible:ring-datavis-blue-30",
    ],
  );

  const thumbnailContent = src ? (
    <img src={src} alt={alt} className={cn(sizeClass, "object-cover")} />
  ) : (
    <div
      data-slot="thumbnail-frame"
      className={cn(
        sizeClass,
        "relative flex items-center justify-center rounded-(--radius-sm) bg-surface-container-lowest text-icon-main",
      )}
    >
      <EmptyThumbnailStroke size={size} />
      <Icon name="image-regular" size={size} tone="inherit" />
    </div>
  );

  if (isInteractive) {
    return (
      <ButtonPrimitive
        type="button"
        className={rootClassName}
        aria-label={ariaLabel}
        aria-pressed={hasSelectedState ? selected : undefined}
        onClick={onClick}
      >
        {thumbnailContent}
      </ButtonPrimitive>
    );
  }

  return <div className={rootClassName}>{thumbnailContent}</div>;
}

function EmptyThumbnailStroke({ size }: { size: ThumbnailSize }) {
  const dimension = thumbnailDimension[size];
  const start = 0.5;
  const end = dimension - 0.5;
  const radius = 3.5;
  const cornerStart = start + radius;
  const cornerEnd = end - radius;
  const dasharray = emptyThumbnailStrokeDasharray[size];
  const dashoffset = emptyThumbnailStrokeDashoffset[size];
  const strokePaths = [
    `M ${cornerStart} ${start} H ${cornerEnd} A ${radius} ${radius} 0 0 1 ${end} ${cornerStart}`,
    `M ${end} ${cornerStart} V ${cornerEnd} A ${radius} ${radius} 0 0 1 ${cornerEnd} ${end}`,
    `M ${cornerEnd} ${end} H ${cornerStart} A ${radius} ${radius} 0 0 1 ${start} ${cornerEnd}`,
    `M ${start} ${cornerEnd} V ${cornerStart} A ${radius} ${radius} 0 0 1 ${cornerStart} ${start}`,
  ];

  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 size-full"
      fill="none"
      viewBox={`0 0 ${dimension} ${dimension}`}
    >
      {strokePaths.map((strokePath) => (
        <path
          key={strokePath}
          data-slot="thumbnail-empty-stroke"
          className="stroke-outline-subtle"
          d={strokePath}
          strokeDasharray={dasharray}
          strokeDashoffset={dashoffset}
          strokeLinecap="butt"
          strokeLinejoin="miter"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}

export { Thumbnail };
export type { ThumbnailSize };
